import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Trash2, Loader2, Users, FolderKanban, Package, Code, Save } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { useToast } from '@/shared/components/ui/use-toast';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { WorkspaceOverviewService, OverviewTemplate } from '../services/workspaceOverviewService';
import { BrainService } from '../services/brainService';
import {
  MIND_LOAD_BUDGET_DEFAULT,
  MIND_LOAD_BUDGET_MAX,
  MIND_LOAD_BUDGET_MIN,
  MIND_LOAD_BUDGET_STEP,
} from '../types/BrainTypes';

const TEMPLATE_ICONS: Record<string, React.ElementType> = {
  team: Users,
  project: FolderKanban,
  product: Package,
  code: Code,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClearAll: () => Promise<void>;
  onRegenerateAll: (customPrompt?: string, maxTokens?: number, temperature?: number) => Promise<void>;
  isGenerating: boolean;
  mode?: 'settings' | 'generate';
  workspaceId: string;
  /** Só administradores alteram o limite e as ações destrutivas (AB#86817). */
  canEdit: boolean;
}

export const OverviewSettingsDialog = ({
  open,
  onOpenChange,
  onClearAll,
  onRegenerateAll,
  isGenerating,
  mode = 'settings',
  workspaceId,
  canEdit,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const lang = (i18n.language?.startsWith('pt') ? 'pt' : 'en') as 'en' | 'pt';

  const [prompt, setPrompt] = useState('');
  const [maxTokens, setMaxTokens] = useState(8000);
  const [temperature, setTemperature] = useState(0.7);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const [templates, setTemplates] = useState<OverviewTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);

  const [loadBudget, setLoadBudget] = useState(MIND_LOAD_BUDGET_DEFAULT);
  const [savedLoadBudget, setSavedLoadBudget] = useState(MIND_LOAD_BUDGET_DEFAULT);
  const [isLoadingBudget, setIsLoadingBudget] = useState(false);
  const [isSavingBudget, setIsSavingBudget] = useState(false);

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    setTemplatesError(false);
    try {
      const data = await WorkspaceOverviewService.getOverviewTemplates();
      setTemplates(data);
    } catch {
      setTemplatesError(true);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (open && mode === 'generate') {
      setPrompt('');
      setMaxTokens(8000);
      setTemperature(0.7);
      setSelectedTemplateKey(null);
      loadTemplates();
    }
  }, [open, mode, loadTemplates]);

  useEffect(() => {
    if (!open || mode !== 'settings') return;
    let cancelled = false;
    setIsLoadingBudget(true);
    BrainService.getMindSettings(workspaceId)
      .then(({ load_budget }) => {
        if (cancelled) return;
        setLoadBudget(load_budget);
        setSavedLoadBudget(load_budget);
      })
      .catch((error) => {
        if (cancelled) return;
        toast({
          title: t('knowledge.mind.loadBudgetLoadError'),
          description: resolveApiErrorMessage(error, t),
          variant: 'destructive',
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBudget(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, workspaceId, toast, t]);

  const isBudgetValid =
    Number.isInteger(loadBudget) && loadBudget >= MIND_LOAD_BUDGET_MIN && loadBudget <= MIND_LOAD_BUDGET_MAX;

  const handleSaveBudget = async () => {
    if (!canEdit || !isBudgetValid) return;
    setIsSavingBudget(true);
    try {
      const saved = await BrainService.saveMindSettings(workspaceId, { load_budget: loadBudget });
      setSavedLoadBudget(saved.load_budget);
      setLoadBudget(saved.load_budget);
      toast({ title: t('common.success'), description: t('knowledge.mind.loadBudgetSaved') });
    } catch (error) {
      toast({
        title: t('knowledge.mind.loadBudgetSaveError'),
        description: resolveApiErrorMessage(error, t),
        variant: 'destructive',
      });
    } finally {
      setIsSavingBudget(false);
    }
  };

  const handleSelectTemplate = (template: OverviewTemplate) => {
    const isDeselecting = selectedTemplateKey === template.key;
    if (isDeselecting) {
      setSelectedTemplateKey(null);
      setPrompt('');
    } else {
      setSelectedTemplateKey(template.key);
      setPrompt(template.prompt[lang] || template.prompt.en);
    }
  };

  const handleClear = async () => {
    setShowClearConfirm(false);
    setIsClearing(true);
    try {
      await onClearAll();
    } finally {
      setIsClearing(false);
    }
  };

  const handleRegenerate = async () => {
    onOpenChange(false);
    const customPrompt = prompt.trim() || undefined;
    await onRegenerateAll(customPrompt, maxTokens, temperature);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[820px] max-h-[92vh] overflow-hidden flex flex-col items-stretch justify-start">
          <DialogHeader>
            <DialogTitle>
              {mode === 'generate' ? t('knowledge.overview.settings_generate_title') : t('knowledge.overview.settings_title')}
            </DialogTitle>
          </DialogHeader>

          {mode === 'generate' ? (
            <div className="mt-0 space-y-3">
              <div>
                <h3 className="text-sm font-semibold">{t('knowledge.overview.settings_ai_title')}</h3>
                <p className="text-xs text-muted-foreground mt-1">{t('knowledge.overview.settings_prompt_description')}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">{t('knowledge.overview.settings_templates_title')}</p>
                {isLoadingTemplates ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {t('knowledge.overview.settings_templates_loading')}
                  </div>
                ) : templatesError ? (
                  <p className="text-xs text-muted-foreground/70 py-1">{t('knowledge.overview.settings_templates_error')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {templates.map((tpl) => {
                      const Icon = TEMPLATE_ICONS[tpl.key];
                      const isSelected = selectedTemplateKey === tpl.key;
                      return (
                        <Badge
                          key={tpl.key}
                          variant={isSelected ? 'default' : 'outline'}
                          className={`cursor-pointer select-none px-3 py-1.5 text-xs font-medium transition-all ${
                            isSelected
                              ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                              : 'hover:bg-accent hover:text-accent-foreground'
                          }`}
                          onClick={() => handleSelectTemplate(tpl)}
                        >
                          {Icon && <Icon className="w-3.5 h-3.5 mr-1.5" />}
                          {tpl.label[lang] || tpl.label.en}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="relative">
                <textarea
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    setSelectedTemplateKey(null);
                  }}
                  placeholder={t('knowledge.overview.settings_prompt_placeholder')}
                  className="w-full h-[380px] rounded-md border bg-transparent px-3 pt-10 pb-2 text-xs font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/50"
                />
                {prompt && (
                  <button
                    type="button"
                    onClick={() => {
                      setPrompt('');
                      setSelectedTemplateKey(null);
                    }}
                    className="absolute top-2 right-8 px-2.5 py-1 text-xs font-medium rounded-md bg-background/95 hover:bg-accent border border-border text-muted-foreground hover:text-foreground transition-colors shadow-sm backdrop-blur-sm z-10"
                  >
                    {t('knowledge.overview.settings_clear_prompt')}
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">{t('knowledge.overview.settings_max_tokens')}</label>
                    <input
                      type="number"
                      value={maxTokens}
                      onChange={(e) => setMaxTokens(Number(e.target.value))}
                      min={1000}
                      max={16000}
                      step={1000}
                      className="w-[80px] h-8 rounded-md border bg-transparent px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">{t('knowledge.overview.settings_temperature')}</label>
                    <input
                      type="number"
                      value={temperature}
                      onChange={(e) => setTemperature(Number(e.target.value))}
                      min={0}
                      max={1}
                      step={0.1}
                      className="w-[65px] h-8 rounded-md border bg-transparent px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={isGenerating || !prompt.trim()}
                  className="bg-[#0B6FC2] hover:bg-[#0B6FC2]/90 text-white"
                >
                  {isGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  {t('knowledge.overview.settings_generate_action')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-6">
              <div className="flex items-end justify-between gap-4 p-4 border rounded-lg">
                <div className="space-y-1">
                  <label htmlFor="mind-load-budget" className="text-sm font-medium">
                    {t('knowledge.mind.loadBudget')}
                  </label>
                  <p className="text-xs text-muted-foreground">{t('knowledge.mind.loadBudgetHint')}</p>
                  <p className="text-[11px] text-muted-foreground/70">
                    {t('knowledge.mind.loadBudgetRange', { min: MIND_LOAD_BUDGET_MIN, max: MIND_LOAD_BUDGET_MAX })}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    id="mind-load-budget"
                    type="number"
                    value={Number.isNaN(loadBudget) ? '' : loadBudget}
                    onChange={(e) => setLoadBudget(e.target.valueAsNumber)}
                    min={MIND_LOAD_BUDGET_MIN}
                    max={MIND_LOAD_BUDGET_MAX}
                    step={MIND_LOAD_BUDGET_STEP}
                    disabled={!canEdit || isLoadingBudget || isSavingBudget}
                    readOnly={!canEdit}
                    aria-invalid={!isBudgetValid}
                    className="w-[100px] h-8 rounded-md border bg-transparent px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                  />
                  {canEdit && (
                    <Button
                      size="sm"
                      onClick={handleSaveBudget}
                      disabled={isLoadingBudget || isSavingBudget || !isBudgetValid || loadBudget === savedLoadBudget}
                    >
                      {isSavingBudget ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                      {t('common.save')}
                    </Button>
                  )}
                </div>
              </div>

              {canEdit && (
              <div>
              <div className="space-y-2 mb-4">
                <h3 className="text-sm font-semibold text-destructive">{t('knowledge.overview.settings_security_title')}</h3>
                <p className="text-xs text-muted-foreground">{t('knowledge.overview.settings_security_description')}</p>
              </div>

              <div className="flex items-center justify-between p-4 border border-destructive/30 rounded-lg">
                <div className="space-y-1">
                  <p className="text-sm font-medium">{t('knowledge.overview.settings_clear_title')}</p>
                  <p className="text-xs text-muted-foreground">{t('knowledge.overview.settings_clear_description')}</p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowClearConfirm(true)}
                  disabled={isClearing}
                  className="shrink-0"
                >
                  {isClearing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                  {t('knowledge.overview.settings_clear_action')}
                </Button>
              </div>
              </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('knowledge.overview.settings_clear_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('knowledge.overview.settings_clear_confirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleClear} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('knowledge.overview.settings_clear_action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
